import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/integrations/aws/resources";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { BilledCell } from "@/components/billed-cell";
import { Truncated } from "@/components/truncated";
import {
  Upload,
  Download,
  FileSpreadsheet,
  AlertTriangle,
  CheckCircle2,
  Wand2,
  Wallet,
  Receipt,
  Building2,
  CircleDollarSign,
} from "lucide-react";
import { StatCard } from "@/components/stat-card";
import { notify } from "@/lib/notify";
import { useConfirm } from "@/components/confirm-dialog";
import { useAuth } from "@/lib/auth";
import { fetchInvoiceStamps, invoiceStampCells, useInvoiceStamps } from "@/lib/invoices";
import type {
  StatementDateOrder,
  VirrginImportResult,
  VirrginImportRowResult,
  VirrginTxnType,
} from "@somasi/shared";
import { VIRRGIN_DEPOSIT_DESCRIPTION } from "@somasi/shared";
import { parseFile, downloadTemplate, type ParsedRow } from "@/lib/csv-import";
import {
  STATEMENT_HEADERS,
  STATEMENT_TEMPLATE_SAMPLE,
  previewRegistryNumber,
  readStatement,
  type StatementReading,
} from "@/lib/virrgin-import";
import {
  useRowSelection,
  BulkBar,
  SelectAllCheckbox,
  RowCheckbox,
} from "@/components/bulk-actions";
import {
  DateRangeFilter,
  EMPTY_DATE_RANGE,
  FilterSelect,
  ListPage,
  ListPageHeader,
  ListPageTable,
  ListSearch,
  ListToolbar,
  SortHeader,
  TableMessageRow,
  TablePagination,
  describeDateRange,
  useDebounced,
  useListSort,
  usePagination,
  type DateRange,
} from "@/components/list-controls";
import { ClientGroupSelect, CompanySelect, searchCompanies } from "@/components/entity-select";
import {
  ListExportDialog,
  type ExportRowSet,
  type ExportScope,
} from "@/components/list-export-dialog";
import { money } from "@/lib/billing";

export const Route = createFileRoute("/_authenticated/virrgin")({
  head: () => ({
    meta: [
      { title: "Virrgin Account — Somasi Ops" },
      {
        name: "description",
        content:
          "Import the Virrgin bank statement and recover FSC expenses from the right client.",
      },
      { property: "og:title", content: "Virrgin Account — Somasi Ops" },
      {
        property: "og:description",
        content:
          "Import the Virrgin bank statement and recover FSC expenses from the right client.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: VirrginPage,
});

type Txn = {
  id: string;
  txn_type: VirrginTxnType;
  txn_date: string;
  description: string | null;
  registration_number: string | null;
  reference: string | null;
  code: string | null;
  amount: number;
  company_id: string | null;
  invoice_id: string | null;
  import_batch: string | null;
};

function VirrginPage() {
  const { canEdit } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [search, setSearch] = useState("");

  /*
   * Five filters rather than one dropdown of canned answers.
   *
   * There used to be a single select holding "unrecovered", "unmatched" and
   * "deposits only" — three different questions in one control, so the one an
   * operator actually has could not be asked: *this client's* fees that are
   * still to go on an invoice. These are the dimensions that question is made
   * of, and they compose.
   *
   * `type` and the two below it overlap in one place, and the handlers keep them
   * consistent rather than leaving a contradiction on screen: a deposit is
   * never recovered and belongs to no company, so picking a status or a company
   * match means charges, and picking deposits clears both.
   */
  const [companyId, setCompanyId] = useState("");
  const [groupId, setGroupId] = useState("");
  // The names behind the two picks, kept from the picker so the export can say
  // which company and group it was narrowed to without looking them up again.
  const [companyName, setCompanyName] = useState("");
  const [groupName, setGroupName] = useState("");
  const [exporting, setExporting] = useState(false);
  const [type, setType] = useState<"all" | "charge" | "deposit">("all");
  const [status, setStatus] = useState<"all" | "unrecovered" | "recovered">("all");
  const [match, setMatch] = useState<"all" | "matched" | "unassigned" | "own_account">("all");
  // The statement date, inclusive at both ends — "between this period".
  const [dates, setDates] = useState<DateRange>(EMPTY_DATE_RANGE);

  const pickType = (next: typeof type) => {
    setType(next);
    if (next === "deposit") {
      setStatus("all");
      setMatch("all");
    }
  };
  const pickStatus = (next: typeof status) => {
    setStatus(next);
    if (next !== "all") setType("charge");
  };
  const pickMatch = (next: typeof match) => {
    setMatch(next);
    if (next !== "all") setType("charge");
  };
  /** A company outside the group just chosen is not a filter anybody meant. */
  const pickGroup = (next: string) => {
    setGroupId(next);
    setCompanyId("");
    if (!next) setGroupName("");
  };
  const pickCompany = (next: string) => {
    setCompanyId(next);
    if (!next) setCompanyName("");
  };

  const narrowed = Boolean(
    companyId ||
    groupId ||
    type !== "all" ||
    status !== "all" ||
    match !== "all" ||
    dates.from ||
    dates.to,
  );
  const clearFilters = () => {
    setCompanyId("");
    setGroupId("");
    setCompanyName("");
    setGroupName("");
    setType("all");
    setStatus("all");
    setMatch("all");
    setDates(EMPTY_DATE_RANGE);
  };

  /*
   * The summary cards are shortcuts into the register: each one shows the rows
   * behind its figure, on the transactions tab, with every other filter cleared
   * so the rows on screen are the ones the number counts.
   */
  const [tab, setTab] = useState("transactions");
  type Shortcut = "all" | "charges" | "unassigned" | "unrecovered";
  const showRows = (shortcut: Shortcut) => {
    clearFilters();
    setSearch("");
    if (shortcut === "charges") setType("charge");
    if (shortcut === "unassigned") pickMatch("unassigned");
    if (shortcut === "unrecovered") pickStatus("unrecovered");
    setTab("transactions");
  };
  const onlyFilter = !companyId && !groupId && !dates.from && !dates.to && !search;
  const shortcutOn: Record<Shortcut, boolean> = {
    all: onlyFilter && type === "all" && status === "all" && match === "all",
    charges: onlyFilter && type === "charge" && status === "all" && match === "all",
    unassigned: onlyFilter && match === "unassigned" && status === "all",
    unrecovered: onlyFilter && status === "unrecovered" && match === "all",
  };

  const refresh = () => qc.invalidateQueries({ queryKey: ["virrgin_transactions"] });

  // Held back so a search is one request when typing stops, not one per key.
  const query = useDebounced(search.trim());
  const dateRange = useDebounced(dates);
  const filters = { query, companyId, groupId, type, status, match, dateRange };
  const sort = useListSort({ column: "txn_date", ascending: false });
  const pager = usePagination({ resetKey: { ...filters, sort: sort.sort } });

  /**
   * The active filters in words, for the export dialog and the PDF's header —
   * the file does not otherwise say which set it holds. Null when nothing is
   * narrowed, which is also what makes the dialog offer "entire" first.
   */
  const filterSummary =
    [
      describeDateRange("dated", dateRange),
      query ? `matching “${query}”` : null,
      companyId ? `for ${companyName || "one company"}` : null,
      groupId ? `in ${groupName || "one client group"}` : null,
      type === "charge" ? "charges only" : type === "deposit" ? "deposits only" : null,
      status === "unrecovered"
        ? "not yet invoiced"
        : status === "recovered"
          ? "recovered on an invoice"
          : null,
      match === "matched"
        ? "matched to a company"
        : match === "unassigned"
          ? "needing a company"
          : match === "own_account"
            ? "on Somasi’s own account"
            : null,
    ]
      .filter(Boolean)
      .join(", ") || null;

  /**
   * The list the filters describe — the table pages it, the export takes it whole.
   *
   * Both the tab filter and the search used to run over a full download of the
   * statement. The tabs are plain column filters, so they move across as they
   * are. The search also matched the *company's* name, which is not a column
   * here — a transaction stores `company_id` — so the typed text is put to the
   * company search first and the ids it names join the OR group.
   *
   * That name-to-id step used to read the whole register on page load, to filter
   * it here. It is a search of its own now — `matchingCompanyIds` below asks the
   * API for the companies whose name matches, which is the same question and one
   * request the size of its answer — and the ids are passed in rather than
   * awaited here: this builder is itself a thenable, so an `async` version of
   * this function would resolve the query as well as the promise around it.
   */
  const matchingCompanyIds = async () =>
    query ? (await searchCompanies(query)).map((c) => c.id) : [];

  const filteredList = (companyIds: readonly string[]) => {
    // The group is the endpoint's own parameter, not a column on this table —
    // the ids it stands for are resolved server-side, because the largest group
    // here holds several hundred companies and sending them all back as an `in`
    // filter is a query string longer than API Gateway will carry.
    const list = api.virrginTransactions
      .list(groupId ? { client_group_id: groupId } : {})
      .select("*")
      .order(sort.sort.column, { ascending: sort.sort.ascending });
    // A statement has many lines on one date and many charges of one amount, so
    // ties fall back to the date and then the Document No. — otherwise a page
    // break can land somewhere different between two requests.
    if (sort.sort.column !== "txn_date") list.order("txn_date", { ascending: false });
    if (sort.sort.column !== "reference") list.order("reference");

    if (companyId) list.eq("company_id", companyId);
    if (dateRange.from) list.gte("txn_date", dateRange.from);
    if (dateRange.to) list.lte("txn_date", dateRange.to);
    if (type !== "all") list.eq("txn_type", type);

    if (status === "unrecovered") list.is("invoice_id", null);
    if (status === "recovered") list.not("invoice_id", "is", null);

    if (match === "matched") list.not("company_id", "is", null);
    // The two halves of "no company": one names a registry number the register
    // does not hold and is waiting for somebody, the other names none at all and
    // never will be — a fee the registry took against Somasi's own account.
    if (match === "unassigned") list.is("company_id", null).not("registration_number", "is", null);
    if (match === "own_account") list.is("company_id", null).is("registration_number", null);

    if (query) {
      list.or([
        { column: "description", op: "ilike", value: `%${query}%` },
        { column: "registration_number", op: "ilike", value: `%${query}%` },
        // The statement's own Document No., which is what somebody holding the
        // statement has in front of them.
        { column: "reference", op: "ilike", value: `%${query}%` },
        ...(companyIds.length
          ? [{ column: "company_id", op: "in" as const, value: [...companyIds] }]
          : []),
      ]);
    }
    return list;
  };

  const { data: result, isLoading } = useQuery({
    queryKey: [
      "virrgin_transactions",
      { ...filters, sort: sort.sort, page: pager.page, pageSize: pager.pageSize },
    ],
    queryFn: async () => {
      const { data, error, count } = await filteredList(await matchingCompanyIds()).page(
        pager.page,
        pager.pageSize,
      );
      if (error) throw error;
      return { rows: (data ?? []) as Txn[], total: count ?? 0 };
    },
    placeholderData: (previous) => previous,
  });

  const filtered = result?.rows ?? [];
  const total = result?.total;

  /**
   * The invoices the recovered charges on this page went onto — number, date and
   * status, read off the invoice rather than copied onto the charge when it was
   * billed. Only the ids this page shows; the register is not read whole.
   */
  const invoiceStamp = useInvoiceStamps(filtered.map((t) => t.invoice_id));

  const sel = useRowSelection(filtered);

  /**
   * The company each row is attached to, and the client group that puts it
   * under — for the ids on this page only.
   *
   * Not `useEntityNames`, which answers with names alone: this column has to
   * print the group as well, because the group is the consequence of the
   * attachment and is the thing an operator is checking when they change it. It
   * is one column further on the same read — a company carries its group's name
   * — rather than a second request per row.
   *
   * Nothing about the group is stored on a transaction, and that is the point:
   * the row names a company, the company names the group, so changing the
   * company moves the fee to the new company's group with nothing to re-stamp,
   * and a company that later moves between groups takes its fees with it.
   */
  const companies = useCompanyLabels(filtered.map((t) => t.company_id));

  /**
   * The float, over every transaction rather than the page.
   *
   * These four figures are the point of the screen — what is left in the
   * account, and how much of what has been drawn is still to be recovered — so
   * they must describe every transaction regardless of which page or filter is
   * showing. That is a sum, not a list, and a list endpoint answers with a page:
   * once a year of statements is in, totalling what came back would be totalling
   * the most recent thousand rows and printing it as the balance. So it is one
   * question to `/virrgin-transactions/summary`, answered on the side of the
   * wire that can see all of them.
   */
  const { data: float } = useQuery({
    queryKey: ["virrgin_transactions", "summary"],
    queryFn: async () => {
      const { data, error } = await api.virrginTransactions.summary();
      if (error) throw error;
      return data;
    },
  });

  const balance = float?.balance ?? 0;

  /**
   * Attaches a company to a charge, moves it to another, or detaches it.
   *
   * `""` clears the attachment, which is what the picker's clear option sends.
   * Worth having rather than leaving a wrong company in place: a fee attached to
   * the wrong client is worse than one attached to nobody, because the first
   * reaches an invoice.
   *
   * Asked first, with the line laid out: the picker sits in a dense table, and a
   * click on the wrong row or the wrong name is what puts a fee on the wrong
   * client's invoice. The question names the document and the company, and
   * shows the figures the operator is checking against the statement.
   */
  const assign = async (txn: Txn, companyId: string, company: CompanyLabel | null) => {
    if ((txn.company_id ?? "") === companyId) return;
    const doc = txn.reference ?? "this charge";
    const current = companies(txn.company_id);
    const ok = await confirm({
      title: companyId
        ? `Attach ${doc} to ${company?.name ?? "this company"}?`
        : `Clear the company from ${doc}?`,
      description: companyId
        ? [
            current ? `It moves off ${current.name}.` : null,
            company?.group
              ? `It will be recovered on ${company.group}’s next invoice.`
              : "It will be recovered on this company’s next invoice.",
          ]
            .filter(Boolean)
            .join(" ")
        : `${current?.name ?? "The company"} will no longer be billed for it, and it goes back to needing a company.`,
      body: <TxnSummary txn={txn} />,
      confirmLabel: companyId ? "Attach" : "Clear company",
      tone: companyId ? "info" : "warning",
    });
    if (!ok) return;
    const { error } = await api.virrginTransactions.update(txn.id, {
      company_id: companyId || null,
    });
    if (error) return notify.error(error);
    notify.success(companyId ? "Company attached" : "Company cleared");
    // The float's own figures move with it — how much is recoverable, and from
    // whom — so the summary is re-asked, not only the page.
    refresh();
  };

  /**
   * The file, for either scope.
   *
   * "Filtered" is the same builder the table pages, so the file cannot describe
   * a different set than the list it was taken from; "all" starts from the bare
   * list with no filter on it, so nothing can silently narrow a file that claims
   * to hold every transaction.
   */
  const exportRows = async (scope: ExportScope): Promise<ExportRowSet> => {
    const list =
      scope === "filtered"
        ? filteredList(await matchingCompanyIds())
        : api.virrginTransactions.list({}).select("*").order("txn_date", { ascending: false });
    const { data, error, truncated } = await list.collect();
    if (error) throw new Error(error.message);
    const txns = (data ?? []) as Txn[];
    // The export's own id set, which is wider than the page on screen — so it
    // resolves its companies rather than reading the table's lookup, which only
    // holds the current page.
    const [labels, stamps] = await Promise.all([
      fetchCompanyLabels(txns.map((t) => t.company_id)),
      fetchInvoiceStamps(txns.map((t) => t.invoice_id)),
    ]);
    const rows = txns.map((t) => {
      const invoice = invoiceStampCells(t.invoice_id ? (stamps[t.invoice_id] ?? null) : null);
      return {
        Date: t.txn_date,
        Type: t.txn_type === "deposit" ? "Deposit" : "Charge",
        Code: t.code ?? "",
        "Document no.": t.reference ?? "",
        Description: t.description ?? "",
        "Registration no.": t.registration_number ?? "",
        Company:
          t.txn_type === "deposit"
            ? "—"
            : ((t.company_id && labels[t.company_id]?.name) ??
              (t.registration_number ? "Needs a company" : "No company named")),
        // The group the fee is recovered under, which is the company's and
        // follows it — not something stored on the row.
        "Client group":
          (t.company_id && labels[t.company_id]?.group) ?? (t.txn_type === "deposit" ? "—" : ""),
        Amount: t.amount,
        Recovered: t.txn_type === "deposit" ? "—" : t.invoice_id ? "Yes" : "No",
        // The invoice as it stands, not as it stood when the charge was
        // recovered: a number may be reissued, a date corrected, and a status
        // moves long after the fee was billed on.
        Invoice: invoice.number,
        "Invoice date": invoice.date,
        "Invoice status": invoice.status,
      };
    });
    return { rows, truncated };
  };

  const rematch = async () => {
    const { data, error } = await api.virrginTransactions.rematch();
    if (error || !data) return notify.error(error ?? new Error("Nothing could be matched"));
    notify.success(
      data.attached
        ? `${data.attached} charge${data.attached === 1 ? "" : "s"} attached` +
            (data.remaining ? ` · ${data.remaining} still unmatched` : "")
        : "No charge could be matched — none of those numbers is on the register yet",
    );
    refresh();
  };

  return (
    <ListPage>
      <ListPageHeader>
        <PageHeader
          title="Virrgin Account"
          description="FSC expenses paid from the Virrgin account, matched to companies and recovered on invoices."
          actions={
            <Button variant="outline" onClick={() => setExporting(true)}>
              <Download className="mr-2 h-4 w-4" /> Export
            </Button>
          }
        />

        <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="Current balance"
            onClick={() => showRows("all")}
            active={tab === "transactions" && shortcutOn.all}
            icon={Wallet}
            value={money(balance)}
            hint={`${money(float?.depositedTotal ?? 0)} deposited · ${money(float?.chargedTotal ?? 0)} drawn`}
            tone={balance < 0 ? "danger" : balance === 0 ? "warn" : "ok"}
          />
          <StatCard
            label="Charges"
            onClick={() => showRows("charges")}
            active={tab === "transactions" && shortcutOn.charges}
            icon={Receipt}
            value={String(float?.charges ?? 0)}
            hint={`${float?.deposits ?? 0} deposit${float?.deposits === 1 ? "" : "s"}`}
          />
          <StatCard
            label="Need a company"
            onClick={() => showRows("unassigned")}
            active={tab === "transactions" && shortcutOn.unassigned}
            icon={Building2}
            value={String(float?.unmatched ?? 0)}
            // The fees the statement named a company for that the register does
            // not hold. Charges naming no company are not in this figure: they
            // are the registry's own fees against Somasi, and nothing an
            // operator does will make them recoverable.
            hint={
              float?.ownAccount
                ? `${float.ownAccount} on Somasi's own account · ${money(float.ownAccountTotal)}`
                : undefined
            }
            tone={float?.unmatched ? "warn" : "ok"}
          />
          <StatCard
            label="Unrecovered value"
            onClick={() => showRows("unrecovered")}
            active={tab === "transactions" && shortcutOn.unrecovered}
            icon={CircleDollarSign}
            value={money(float?.unrecoveredTotal ?? 0)}
            tone={float?.unrecoveredTotal ? "warn" : "ok"}
          />
        </div>
      </ListPageHeader>

      {/* The height is handed down through the tabs so the register's card has
          something left to take. The panels are `hidden` by default and laid out
          only when active: Radix closes a panel with the `hidden` attribute, and
          the UA rule behind it loses to any class, so a panel carrying `flex`
          would stay visible alongside the open one.

          Only the register fills the viewport. The import wizard and the deposit
          form are documents rather than lists — they scroll as they always did,
          which is what a long form wants. */}
      <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
        {/* The tabs lead, with the register's search beside them; the filters
            that narrow the register come underneath, inside its tab. The other
            two tabs are forms, so the search is only there for the register. */}
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <TabsList className="shrink-0">
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="import">Import statement</TabsTrigger>
            {canEdit && <TabsTrigger value="deposit">Record a deposit</TabsTrigger>}
          </TabsList>
          {tab === "transactions" && (
            <ListSearch
              value={search}
              onChange={setSearch}
              placeholder="Search registration no., document no., company or description…"
            />
          )}
        </div>

        <TabsContent
          value="transactions"
          className="mt-4 hidden min-h-0 flex-1 flex-col gap-3 data-[state=active]:flex"
        >
          {/* `mb-0` because the tab panel spaces its children with a gap already. */}
          <ListToolbar className="mb-0">
            {/* Both pickers search the API rather than listing a table they
                downloaded — the register runs to hundreds of companies, and the
                one being looked for is reached by typing its name. The company
                picker narrows to the chosen group, so the two read as one
                question rather than two that can contradict each other. */}
            <ClientGroupSelect
              value={groupId}
              onChange={pickGroup}
              onPick={(option) => setGroupName(option.label)}
              clearable
              clearLabel="Any client group"
              placeholder="Any client group"
              className="w-52"
              triggerClassName="h-9"
            />
            <CompanySelect
              value={companyId}
              onChange={pickCompany}
              onPick={(option) => setCompanyName(option.label)}
              filters={groupId ? { clientGroupId: groupId } : {}}
              showClientGroup={!groupId}
              clearable
              clearLabel="Any company"
              placeholder="Any company"
              className="w-52"
              triggerClassName="h-9"
              contentClassName="w-80 p-0"
            />

            <DateRangeFilter label="Date" value={dates} onChange={setDates} className="w-auto" />

            <FilterSelect
              value={type}
              onChange={(v) => pickType(v as typeof type)}
              className="w-40"
              options={[
                { value: "all", label: "Charges & deposits" },
                { value: "charge", label: "Charges only" },
                { value: "deposit", label: "Deposits only" },
              ]}
            />

            <FilterSelect
              value={status}
              onChange={(v) => pickStatus(v as typeof status)}
              options={[
                { value: "all", label: "Any status" },
                { value: "unrecovered", label: "Not yet invoiced" },
                { value: "recovered", label: "Recovered on an invoice" },
              ]}
            />

            <FilterSelect
              value={match}
              onChange={(v) => pickMatch(v as typeof match)}
              className="w-52"
              options={[
                { value: "all", label: "Matched or not" },
                { value: "matched", label: "Matched to a company" },
                { value: "unassigned", label: "Needs a company" },
                { value: "own_account", label: "Somasi’s own account" },
              ]}
            />

            {/* Only shown when there is something for it to do. The usual
                reason a charge is unmatched is that the company was
                incorporated after the company list was last imported, so this
                is the button to press once that list has caught up —
                re-importing the statement will not do it, every line of it
                being a duplicate by then. */}
            {canEdit && (float?.unmatched ?? 0) > 0 && (
              <Button variant="outline" size="sm" onClick={rematch}>
                <Wand2 className="mr-2 h-4 w-4" />
                Match {float?.unmatched} by registry no.
              </Button>
            )}

            {narrowed && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </ListToolbar>

          {canEdit && (
            <BulkBar
              sel={sel}
              table="virrgin_transactions"
              entityLabel="transaction"
              onDone={refresh}
            />
          )}

          <ListPageTable>
            <Table containerClassName="min-h-0 flex-1">
              <TableHeader sticky>
                <TableRow>
                  {canEdit && (
                    <TableHead className="w-10">
                      <SelectAllCheckbox sel={sel} />
                    </TableHead>
                  )}
                  <SortHeader sort={sort} column="txn_date">
                    Date
                  </SortHeader>
                  <SortHeader sort={sort} column="txn_type">
                    Type
                  </SortHeader>
                  <SortHeader sort={sort} column="reference">
                    Document no.
                  </SortHeader>
                  <SortHeader sort={sort} column="registration_number">
                    Registration no.
                  </SortHeader>
                  {/* A company id resolved to a name, and the status is the
                      invoice the charge went onto — neither orders usefully as
                      the column the row stores. */}
                  <TableHead>Company</TableHead>
                  <SortHeader sort={sort} column="description">
                    Description
                  </SortHeader>
                  <SortHeader sort={sort} column="amount" className="text-right" align="right">
                    Amount
                  </SortHeader>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableMessageRow colSpan={canEdit ? 9 : 8}>Loading…</TableMessageRow>
                ) : filtered.length === 0 ? (
                  <TableMessageRow colSpan={canEdit ? 9 : 8}>
                    {query || narrowed
                      ? "No transactions match these filters."
                      : "No transactions. Import a statement to get started."}
                  </TableMessageRow>
                ) : (
                  filtered.map((t) => (
                    <TableRow key={t.id} data-state={sel.isSelected(t.id) ? "selected" : undefined}>
                      {canEdit && (
                        <TableCell>
                          <RowCheckbox sel={sel} id={t.id} />
                        </TableCell>
                      )}
                      <TableCell>{t.txn_date}</TableCell>
                      <TableCell>
                        {t.txn_type === "deposit" ? (
                          <Badge className="bg-success text-success-foreground hover:bg-success/90">
                            Deposit
                          </Badge>
                        ) : (
                          <Badge variant="outline">Charge</Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{t.reference ?? "—"}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {t.registration_number ?? "—"}
                      </TableCell>
                      <TableCell>
                        <CompanyCell
                          txn={t}
                          label={companies(t.company_id)}
                          canEdit={canEdit}
                          onChange={(id, company) => assign(t, id, company)}
                        />
                      </TableCell>
                      <TableCell className="max-w-sm text-sm text-muted-foreground">
                        <Truncated text={t.description} />
                      </TableCell>
                      <TableCell
                        className={`text-right tabular-nums ${t.txn_type === "deposit" ? "text-success" : ""}`}
                      >
                        {t.txn_type === "deposit" ? "+" : "−"}
                        {money(Math.abs(Number(t.amount)))}
                      </TableCell>
                      <TableCell>
                        {/* A deposit tops the float up and is never recovered,
                            so it has no stamp to show rather than an empty
                            one. */}
                        {t.txn_type === "deposit" ? (
                          <span className="text-sm text-muted-foreground">—</span>
                        ) : (
                          <BilledCell
                            invoiceId={t.invoice_id}
                            stamp={invoiceStamp(t.invoice_id)}
                            billed="Recovered"
                            unbilled="Unrecovered"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              pagination={pager}
              total={total}
              loading={isLoading}
              unit="transaction"
            />
          </ListPageTable>
        </TabsContent>

        <TabsContent value="deposit" className="mt-4 min-h-0 flex-1 overflow-y-auto">
          <DepositPanel balance={balance} onSaved={refresh} />
        </TabsContent>

        <TabsContent value="import" className="mt-4 min-h-0 flex-1 overflow-y-auto">
          <ImportPanel onImported={refresh} canEdit={canEdit} />
        </TabsContent>
      </Tabs>

      {/* Mounted on opening, so its default scope reads the filters as they
          are at that moment. */}
      {exporting && (
        <ListExportDialog
          open
          onOpenChange={setExporting}
          title="Export Virrgin transactions"
          description="Every column the statement holds, with the company each charge is recovered from and the invoice it went onto."
          filenameBase="somasi-virrgin-transactions"
          reportTitle="Virrgin transactions"
          unitPlural="transactions"
          entireLabel="All transactions"
          entireHint="Every charge and deposit ever imported, ignoring the filters on the list."
          filteredCount={total}
          totalCount={float ? float.charges + float.deposits : undefined}
          filterSummary={filterSummary}
          fetchRows={exportRows}
          columns={EXPORT_COLUMNS}
          pdfCols={[
            { header: "Date", dataKey: "Date" },
            { header: "Type", dataKey: "Type" },
            { header: "Document no.", dataKey: "Document no." },
            { header: "Registration no.", dataKey: "Registration no." },
            { header: "Company", dataKey: "Company" },
            { header: "Client group", dataKey: "Client group" },
            { header: "Description", dataKey: "Description" },
            { header: "Amount", dataKey: "Amount" },
            // The printed sheet carries the invoice and where it has got to, in
            // place of the Yes/No the spreadsheet still has: a number and a
            // status say strictly more, and eleven columns across one landscape
            // page is too many to read.
            { header: "Invoice", dataKey: "Invoice" },
            { header: "Status", dataKey: "Invoice status" },
          ]}
        />
      )}
    </ListPage>
  );
}

/** The spreadsheet's columns, pinned so their order is not an accident of the row objects. */
const EXPORT_COLUMNS = [
  "Date",
  "Type",
  "Code",
  "Document no.",
  "Description",
  "Registration no.",
  "Company",
  "Client group",
  "Amount",
  "Recovered",
  "Invoice",
  "Invoice date",
  "Invoice status",
];

/** A company as this screen prints it: its name, and the group it sits in. */
interface CompanyLabel {
  name: string;
  group: string | null;
}

/**
 * The companies behind a set of ids, name and client group together.
 *
 * One read of the ids actually in hand — the register runs to hundreds of
 * companies and this screen needs the handful its rows name. Shared by the hook
 * below and by the export, whose id set is the whole filtered list rather than
 * the page.
 */
async function fetchCompanyLabels(
  ids: readonly (string | null)[],
): Promise<Record<string, CompanyLabel>> {
  const wanted = [...new Set(ids.filter((id): id is string => !!id))];
  if (wanted.length === 0) return {};

  const { data, error } = await api.companies
    .list()
    .select("id, legal_name, client_group_name")
    .in("id", wanted)
    .all();
  if (error) throw new Error(error.message);

  const labels: Record<string, CompanyLabel> = {};
  for (const row of data ?? []) {
    labels[row.id] = { name: row.legal_name, group: row.client_group_name ?? null };
  }
  return labels;
}

/**
 * The companies behind the ids on the page, name and client group together.
 *
 * Keyed on the id set rather than the page number, like `useEntityNames` — which
 * this would be, if the group were not needed beside the name. A company carries
 * its group's name as a column, so both come back from one read of the ids
 * actually in hand.
 */
function useCompanyLabels(ids: readonly (string | null)[]) {
  const fingerprint = [...new Set(ids.filter((id): id is string => !!id))].sort().join(",");
  const wanted = useMemo(() => (fingerprint ? fingerprint.split(",") : []), [fingerprint]);

  const { data } = useQuery({
    queryKey: ["companies_labels", wanted],
    enabled: wanted.length > 0,
    queryFn: () => fetchCompanyLabels(wanted),
    staleTime: 5 * 60 * 1000,
    placeholderData: (previous) => previous,
  });

  return useMemo(() => {
    const labels = data ?? {};
    return (id: string | null): CompanyLabel | null => (id ? (labels[id] ?? null) : null);
  }, [data]);
}

/**
 * Which company a fee is recovered from — and changing it.
 *
 * Three states, because they are three different things:
 *
 *  - A **deposit** belongs to nobody. There is nothing to attach.
 *
 *  - A **recovered** charge is fixed. Its company is already on an issued
 *    invoice, and moving it here would not move the invoice line: the two would
 *    simply disagree about who was billed. Releasing it means deleting that
 *    invoice, which clears the stamp and makes this editable again.
 *
 *  - Anything else is editable, including a charge that already carries a
 *    company — a registry number can name the wrong one, and the number is only
 *    what the FSC believed. Clearing it is offered too: a fee attached to the
 *    wrong client is worse than one attached to nobody, because the first
 *    reaches an invoice.
 *
 * The client group is printed under the name rather than stored anywhere. It is
 * the company's group, so it follows the attachment — which is why the picker
 * shows each candidate's group as it is chosen: moving a fee between companies
 * usually moves it between groups too, and that is what decides which group's
 * invoice it turns up on.
 */
function CompanyCell({
  txn,
  label,
  canEdit,
  onChange,
}: {
  txn: Txn;
  label: CompanyLabel | null;
  canEdit: boolean;
  onChange: (companyId: string, company: CompanyLabel | null) => void;
}) {
  if (txn.txn_type === "deposit") {
    return <span className="text-sm text-muted-foreground">Top-up to the float</span>;
  }

  const group = label?.group ? (
    <div className="text-xs text-muted-foreground">{label.group}</div>
  ) : null;

  if (txn.invoice_id) {
    return (
      <div className="space-y-0.5">
        <div className="font-medium">{label?.name ?? "—"}</div>
        {group}
        <div className="text-xs text-muted-foreground">Fixed — already on an invoice</div>
      </div>
    );
  }

  if (!canEdit) {
    if (label) {
      return (
        <div className="space-y-0.5">
          <div className="font-medium">{label.name}</div>
          {group}
        </div>
      );
    }
    return txn.registration_number ? (
      <Badge variant="outline">Needs a company</Badge>
    ) : (
      <span className="text-sm text-muted-foreground">No company named</span>
    );
  }

  return (
    <div className="space-y-1">
      <CompanySelect
        value={txn.company_id ?? ""}
        // A pick arrives through `onPick`, which carries the name and group the
        // confirmation needs; `onChange` is left to the clear option alone.
        onChange={(id) => {
          if (!id) onChange("", null);
        }}
        onPick={(option) => onChange(option.id, { name: option.label, group: option.hint ?? null })}
        // Clearing is a real answer here — see above.
        clearable
        clearLabel="No company"
        // A line that named no registry number named no company: the registry
        // charged Somasi's own account, and there is nothing to look up. The
        // picker stays, because now and then one of them is recoverable after
        // all.
        placeholder={txn.registration_number ? "Assign company…" : "No company named"}
        triggerClassName="h-8"
        className="w-56"
        contentClassName="w-80 p-0"
      />
      {group}
    </div>
  );
}

/** One statement line as the attach confirmation shows it. */
function TxnSummary({ txn }: { txn: Txn }) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Document no.</TableHead>
            <TableHead>Registration no.</TableHead>
            <TableHead className="text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="whitespace-nowrap">{txn.txn_date}</TableCell>
            <TableCell>{txn.txn_type === "deposit" ? "Deposit" : "Charge"}</TableCell>
            <TableCell className="font-mono text-xs">{txn.reference ?? "—"}</TableCell>
            <TableCell className="font-mono text-xs">{txn.registration_number ?? "—"}</TableCell>
            <TableCell className="text-right tabular-nums">
              {money(Math.abs(Number(txn.amount)))}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Money paid into the float. A deposit carries no company and is never billed —
 * it only raises the balance that the registry's charges draw down.
 */
function DepositPanel({ balance, onSaved }: { balance: number; onSaved: () => void }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const value = Number(amount);
  const valid = date !== "" && Number.isFinite(value) && value > 0;

  const save = async () => {
    if (!valid) return notify.error("Enter a date and an amount greater than zero");
    setBusy(true);
    const { error } = await api.virrginTransactions.create({
      txn_type: "deposit" as VirrginTxnType,
      txn_date: date,
      description: description.trim() || VIRRGIN_DEPOSIT_DESCRIPTION,
      registration_number: null,
      reference: reference.trim() || null,
      code: null,
      amount: value,
      company_id: null,
      notes: null,
    });
    setBusy(false);
    if (error) return notify.error(error);
    notify.success(`Deposit of ${money(value)} recorded`);
    setAmount("");
    setReference("");
    setDescription("");
    onSaved();
  };

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle className="text-base">Record a deposit into the Virrgin account</CardTitle>
        <CardDescription>
          The balance stands at {money(balance)}. A deposit raises it; the registry&rsquo;s charges
          draw it down. Deposits are never billed to a client.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="deposit-date" required>
              Date
            </Label>
            <Input
              id="deposit-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="deposit-amount" required>
              Amount (USD)
            </Label>
            <Input
              id="deposit-amount"
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="deposit-ref">Reference</Label>
          <Input
            id="deposit-ref"
            placeholder="Wire reference, receipt number…"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="deposit-desc">Description</Label>
          <Input
            id="deposit-desc"
            placeholder={VIRRGIN_DEPOSIT_DESCRIPTION}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <Button onClick={save} disabled={!valid || busy}>
          {busy ? "Recording…" : "Record deposit"}
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * The statement, from the file to the register.
 *
 * Three steps, and the middle one is the point: the file is read into rows, the
 * rows are sent to be *read against the register* with nothing written, and what
 * comes back is what the operator approves. The commit then sends the same rows
 * to the same endpoint. The preview and the write are one code path, so the
 * screen cannot promise one outcome and the table receive another.
 *
 * This is also where two full downloads used to happen. Matching a line to a
 * company needed every company's registry numbers, and recognising one already
 * imported needed every row ever stored — both read into the browser, both now
 * past the thousand-row ceiling a list endpoint answers with, which would have
 * made the second one wave known duplicates through. Both are scans behind the
 * import endpoint now.
 */
function ImportPanel({ onImported, canEdit }: { onImported: () => void; canEdit: boolean }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");
  /** Kept so changing the date order re-reads the same file rather than asking for it again. */
  const [parsed, setParsed] = useState<ParsedRow[] | null>(null);
  const [order, setOrder] = useState<StatementDateOrder | "file">("file");
  const [reading, setReading] = useState<StatementReading | null>(null);
  const [preview, setPreview] = useState<VirrginImportResult | null>(null);
  const [busy, setBusy] = useState(false);

  const read = async (rows: ParsedRow[], chosen: StatementDateOrder | "file") => {
    const statement = readStatement(rows, chosen === "file" ? undefined : chosen);
    setReading(statement);
    setPreview(null);

    if (statement.rows.length === 0) {
      return notify.error("No transactions in that file — check the column headings");
    }

    setBusy(true);
    const { data, error } = await api.virrginTransactions.import(statement.rows, { dryRun: true });
    setBusy(false);
    if (error || !data) return notify.error(error ?? new Error("The statement could not be read"));

    setPreview(data);
    const { counts } = data;
    notify.success(
      `${statement.rows.length} rows read · ${counts.matched} matched to a company` +
        (counts.duplicate ? ` · ${counts.duplicate} already imported` : ""),
    );
  };

  const handleFile = async (file: File) => {
    setFileName(file.name);
    let rows: ParsedRow[] = [];
    try {
      rows = await parseFile(file);
    } catch (e) {
      return notify.error(`Could not read the file: ${(e as Error).message}`);
    }
    setParsed(rows);
    await read(rows, order);
  };

  const reorder = async (chosen: StatementDateOrder | "file") => {
    setOrder(chosen);
    if (parsed) await read(parsed, chosen);
  };

  const discard = () => {
    setParsed(null);
    setReading(null);
    setPreview(null);
    setFileName("");
  };

  const commit = async () => {
    if (!reading || !preview) return;
    const writing = preview.counts.ready + preview.counts.repaired;
    if (writing === 0) return notify.error("Nothing left to import from this statement");

    setBusy(true);
    const { data, error } = await api.virrginTransactions.import(reading.rows);
    setBusy(false);
    if (error || !data)
      return notify.error(error ?? new Error("The statement could not be stored"));

    const { counts } = data;
    notify.success(
      `Imported ${counts.imported} transactions` +
        (counts.repaired ? ` · ${counts.repaired} earlier rows repaired` : "") +
        (counts.unmatched ? ` · ${counts.unmatched} still to assign` : ""),
    );
    discard();
    onImported();
  };

  /** The line the file held for a result, so a rejection can show its own row. */
  const lineFor = (result: VirrginImportRowResult) => reading?.lines[result.index];

  const willWrite = (preview?.counts.ready ?? 0) + (preview?.counts.repaired ?? 0);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">1 · Upload the Virrgin statement</CardTitle>
          <CardDescription>
            The FSC&rsquo;s statement as CSV or Excel, with its own headings —{" "}
            {STATEMENT_HEADERS.join(", ")}. The company comes from the registration number in the
            Description column, the code tells a registry charge from a replenishment, and the
            balance brought forward is left out.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = "";
            }}
          />
          <Button onClick={() => fileRef.current?.click()} disabled={!canEdit || busy}>
            <Upload className="mr-2 h-4 w-4" /> Choose file
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              downloadTemplate(
                "virrgin-statement-template.csv",
                STATEMENT_HEADERS,
                STATEMENT_TEMPLATE_SAMPLE,
              )
            }
          >
            <Download className="mr-2 h-4 w-4" /> Download template
          </Button>

          {/* The date column is the one thing a file cannot always settle on its
              own, so it is asked here rather than guessed per row — see
              `detectDateOrder`. Left on "as the file reads" for every statement
              that proves its own order, which is nearly all of them. */}
          <div className="space-y-2">
            <Label htmlFor="virrgin-date-order">Dates</Label>
            <Select value={order} onValueChange={(v) => reorder(v as StatementDateOrder | "file")}>
              <SelectTrigger id="virrgin-date-order" className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="file">As the file reads</SelectItem>
                <SelectItem value="mdy">M/D/YYYY — as the FSC prints</SelectItem>
                <SelectItem value="dmy">D/M/YYYY</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {fileName && (
            <span className="pb-2 text-sm text-muted-foreground">
              <FileSpreadsheet className="mr-1 inline h-4 w-4" />
              {fileName}
            </span>
          )}
        </CardContent>
      </Card>

      {reading && reading.orderConflict && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>The dates in this file contradict each other</AlertTitle>
          <AlertDescription>
            {reading.orderConflict} cannot both be dates in the same column. Fix the column in the
            file, or pick the order to read it in above — but check the dates below before
            importing.
          </AlertDescription>
        </Alert>
      )}

      {reading && reading.orderAssumed && !reading.orderConflict && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No row in this file proves which number is the month</AlertTitle>
          <AlertDescription>
            Every day in it is the twelfth or earlier, so {reading.order === "mdy" ? "M/D" : "D/M"}{" "}
            has been assumed — the order the FSC prints. Check the dates below, and change the order
            above if they are a month out.
          </AlertDescription>
        </Alert>
      )}

      {preview && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="Charges to import"
              value={String(preview.counts.charges)}
              hint={`${preview.counts.deposits} replenishment${preview.counts.deposits === 1 ? "" : "s"}`}
            />
            <StatCard
              label="Matched to a company"
              value={`${preview.counts.matched} of ${preview.counts.charges}`}
              // The rest are not all gaps: a charge naming no company at all is
              // the registry billing Somasi's own account, which no amount of
              // matching will change.
              hint={
                preview.counts.ownAccount
                  ? `${preview.counts.ownAccount} on Somasi's own account`
                  : undefined
              }
              tone={preview.counts.unmatched ? "warn" : "ok"}
            />
            <StatCard
              label="Already imported"
              value={String(preview.counts.duplicate)}
              hint={preview.counts.repaired ? `${preview.counts.repaired} to repair` : undefined}
            />
            <StatCard
              label="Rejected"
              value={String(preview.counts.rejected)}
              tone={preview.counts.rejected ? "warn" : "ok"}
            />
          </div>

          {preview.counts.unmatched > 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>
                {preview.counts.unmatched} charge
                {preview.counts.unmatched === 1 ? " has" : "s have"} no matching company
              </AlertTitle>
              <AlertDescription>
                They import all the same — a fee left out of the register is one that is certain
                never to be recovered. Assign them from the Transactions tab and they become
                recoverable on the next invoice.
              </AlertDescription>
            </Alert>
          )}

          {reading && reading.dropped.length > 0 && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>
                {reading.dropped.length} line{reading.dropped.length === 1 ? "" : "s"} left out
              </AlertTitle>
              <AlertDescription>
                {reading.dropped.map((line) => `row ${line.sheetRow} — ${line.dropped}`).join("; ")}
                .
              </AlertDescription>
            </Alert>
          )}

          <div className="overflow-x-auto rounded-md border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Document no.</TableHead>
                  <TableHead>Registration no.</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Result</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.rows.map((r) => {
                  const line = lineFor(r);
                  const spent = r.outcome === "duplicate" || r.outcome === "rejected";
                  return (
                    <TableRow key={r.index} className={spent ? "opacity-60" : undefined}>
                      <TableCell>
                        {r.txn_date || (
                          <span className="text-warning">{line?.cells.txn_date || "—"}</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.code ?? "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{r.reference ?? "—"}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {r.registration_number ??
                          (line ? previewRegistryNumber(line.cells) : null) ??
                          "—"}
                      </TableCell>
                      <TableCell>
                        {r.txn_type === "deposit" ? (
                          <span className="text-sm text-muted-foreground">Top-up to the float</span>
                        ) : r.company_name ? (
                          <span className="font-medium">{r.company_name}</span>
                        ) : r.registration_number ? (
                          <Badge className="bg-warning text-warning-foreground hover:bg-warning/90">
                            Unmatched
                          </Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">No company named</span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-xs text-sm text-muted-foreground">
                        <Truncated text={r.description} />
                      </TableCell>
                      <TableCell
                        className={`text-right tabular-nums ${r.txn_type === "deposit" ? "text-success" : ""}`}
                      >
                        {Number.isFinite(r.amount) && r.amount > 0
                          ? `${r.txn_type === "deposit" ? "+" : "−"}${money(r.amount)}`
                          : (line?.cells.amount ?? "—")}
                      </TableCell>
                      <TableCell className="text-xs">
                        <Outcome result={r} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={discard} disabled={busy}>
              Discard
            </Button>
            <Button onClick={commit} disabled={busy || !canEdit || willWrite === 0}>
              {busy ? "Importing…" : `Import ${willWrite} transactions`}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/** What the import made of one line, in the words the preview shows. */
function Outcome({ result }: { result: VirrginImportRowResult }) {
  if (result.outcome === "rejected") {
    return <span className="text-warning">Skipped — {result.reason}</span>;
  }
  if (result.outcome === "duplicate") {
    return <span className="text-muted-foreground">{result.reason}</span>;
  }
  if (result.outcome === "repaired") {
    return <span className="text-success">Repairs an earlier row</span>;
  }
  // A charge that named a registry number nobody holds is a warning; one that
  // named none is simply what it is, and colouring it as a problem is how a
  // statement full of perfectly ordinary account fees came to look alarming.
  const settled = result.company_id || result.txn_type === "deposit" || !result.registration_number;
  return (
    <span className={settled ? "text-success" : ""}>
      <CheckCircle2 className="mr-1 inline h-3 w-3" />
      Ready
      {result.reason ? (
        <span className={result.registration_number ? "text-warning" : "text-muted-foreground"}>
          {" "}
          — {result.reason}
        </span>
      ) : null}
    </span>
  );
}
